// ==============================
// Imports
// ==============================

// MUI Components
import { Edit, Search } from '@mui/icons-material';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TablePagination,
  // Avatar,
  IconButton,
  Typography,
  Box,
  TextField,
  InputAdornment,
  FormControl,
  Select,
  MenuItem,
  // Divider,
  Tooltip,
  Button,
  // TableSortLabel,
} from '@mui/material';
import { TableNoData, TableSpacerRow } from './common/TableEmptyState';
import TableLoader from './common/TableLoader';
import {
  MANAGEMENT_TABLE_HEADER_COLOR,
  ManagementTableActionCell,
  ManagementTableHeaderCell,
  managementTableRowSx,
} from './common/ManagementTable';
import FilterAltOffIcon from '@mui/icons-material/FilterAltOff';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';

// Libraries

// Local Components
import { serviceTypeColors, trialStatusColors } from '../utils/colorHandler';
import ColorChip from './common/ColorChip';

// Hook
import { useTrialBookingTable } from '../hooks/useTrialBookingTable';
import { Subtype } from '../types/TrialBookingTable.type';
import dayjs from 'dayjs';
import TrialBookingInfoDialog from './FormDialog/TrialBookingInfoDialog';
import { useTranslation } from '../i18n';

const PLACEHOLDER_PHONE = '0909999999';

const isMissingContactValue = (value: string | null | undefined) => {
  const normalizedValue = value?.trim().toLowerCase();
  return (
    !normalizedValue ||
    ['none', 'unknown', 'n/a', 'na', '-', '---'].includes(normalizedValue)
  );
};

const ContactValue = ({
  value,
  type,
}: {
  value: string | null | undefined;
  type: 'email' | 'phone';
}) => {
  const { t } = useTranslation();
  const normalizedValue = value?.trim() || '';

  if (isMissingContactValue(value)) {
    return <span style={{ opacity: 0.4 }}>{t('trialBooking.none')}</span>;
  }

  if (type === 'phone' && normalizedValue === PLACEHOLDER_PHONE) {
    return <span style={{ opacity: 0.4 }}>{normalizedValue}</span>;
  }

  const href =
    type === 'phone'
      ? `https://zalo.me/${normalizedValue.replace(/[^\d+]/g, '')}`
      : `https://mail.google.com/mail/?view=cm&fs=1&to=${encodeURIComponent(normalizedValue)}`;

  return (
    <Tooltip
      title={t('trialBooking.clickToOpen', {
        app: type === 'phone' ? 'Zalo' : 'Gmail',
      })}
    >
      <a
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        style={{
          textDecoration: 'underline',
          color: 'inherit',
          cursor: 'pointer',
        }}
      >
        {normalizedValue}
      </a>
    </Tooltip>
  );
};

const TrialBookingTable = () => {
  const {
    trialBookingList,
    page,
    limit,
    totalItems,
    searchTerm,
    subTypeFilter,
    resultFilter,
    setResultFilter,
    filterDate,
    setFilterDate,
    emptyRows,
    handleChangePage,
    handleChangeRowsPerPage,
    handleSearchChange,
    handleFilterSubtypeChange,
    clearFilters,
    fetchTrialBookingList,
    setPage,
    subtypeOptions,
    openBookingInfoDialog,
    setOpenBookingInfoDialog,
    selectedTrialBooking,
    setSelectedTrialBooking,
    handleCloseTrialBookingDialog,
    calculateAge,
    formatResultString,
    handleDownloadTrialBookingList,
    downloadLoading,
    loading,
    lastSyncTime,
    highlightedRow,
    setHighlightedRow,
  } = useTrialBookingTable();

  const { t } = useTranslation();

  // ==============================
  // Render
  // ==============================
  return (
    <Paper>
      <Box
        p={2}
        display="flex"
        justifyContent="space-between"
        alignItems="center"
      >
        <TextField
          size="small"
          placeholder={t('trialBooking.searchPlaceholder')}
          value={searchTerm}
          onChange={handleSearchChange}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              fetchTrialBookingList();
              setPage(1);
            }
          }}
          sx={{ width: 350 }}
          slotProps={{
            input: {
              startAdornment: (
                <InputAdornment position="start">
                  <Search />
                </InputAdornment>
              ),
            },
          }}
        />

        <Box display={'flex'} sx={{ gap: 2 }}>
          <FormControl size="small" sx={{ minWidth: 150 }}>
            <Select
              displayEmpty
              value={subTypeFilter?.id.toString() ?? '-1'}
              onChange={handleFilterSubtypeChange}
            >
              <MenuItem value="-1">{t('trialBooking.allServices')}</MenuItem>
              {subtypeOptions.map((option: Subtype) => (
                <MenuItem key={option.id} value={option.id}>
                  {option.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 150 }}>
            <Select
              displayEmpty
              value={resultFilter}
              onChange={(event) => setResultFilter(event.target.value)}
            >
              <MenuItem value="ALL">{t('trialBooking.allResult')}</MenuItem>
              <MenuItem value="ENROLL">
                {t('trialBooking.resultEnroll')}
              </MenuItem>
              <MenuItem value="PENDING">
                {t('trialBooking.resultPending')}
              </MenuItem>
              <MenuItem value="NOT_COME">
                {t('trialBooking.resultNotCome')}
              </MenuItem>
              <MenuItem value="CONSIDER">
                {t('trialBooking.resultConsider')}
              </MenuItem>
            </Select>
          </FormControl>

          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <DatePicker
              format="DD/MM/YYYY"
              label={t('trialBooking.filterByTrialDate')}
              value={filterDate}
              onChange={(newDate) => setFilterDate(newDate)}
              slotProps={{
                textField: {
                  size: 'small',
                },
                field: {
                  clearable: true,
                  onClear: () => setFilterDate(null),
                },
              }}
            />
          </LocalizationProvider>

          {/* Clear filters button */}
          {searchTerm ||
          subTypeFilter.id !== -1 ||
          resultFilter !== 'ALL' ||
          filterDate ? (
            <Button
              size="small"
              variant="contained"
              color="info"
              onClick={clearFilters}
              startIcon={<FilterAltOffIcon />}
            >
              {t('trialBooking.clear')}
            </Button>
          ) : null}

          <Tooltip title={t('trialBooking.downloadList')}>
            <IconButton
              onClick={handleDownloadTrialBookingList}
              loading={downloadLoading}
              disabled={trialBookingList.length === 0 || downloadLoading}
            >
              <FileDownloadIcon />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>
      <TableContainer>
        <Table size="small" padding="checkbox">
          {/* ********** */}
          {/* HEADER */}
          {/* ********** */}
          <TableHead>
            <TableRow sx={{ backgroundColor: MANAGEMENT_TABLE_HEADER_COLOR }}>
              <ManagementTableHeaderCell>
                {t('trialBooking.colBookingDate')}
              </ManagementTableHeaderCell>
              <ManagementTableHeaderCell>
                {t('trialBooking.colFullName')}
              </ManagementTableHeaderCell>
              <ManagementTableHeaderCell>
                {t('trialBooking.colAge')}
              </ManagementTableHeaderCell>
              <ManagementTableHeaderCell>
                {t('trialBooking.colDateOfBirth')}
              </ManagementTableHeaderCell>
              <ManagementTableHeaderCell>
                {t('trialBooking.colEmail')}
              </ManagementTableHeaderCell>
              <ManagementTableHeaderCell>
                {t('trialBooking.colPhone')}
              </ManagementTableHeaderCell>
              <ManagementTableHeaderCell>
                {t('trialBooking.colParent')}
              </ManagementTableHeaderCell>
              <ManagementTableHeaderCell>
                {t('trialBooking.colProgram')}
              </ManagementTableHeaderCell>
              <ManagementTableHeaderCell>
                {t('trialBooking.colTimeDateOfTrial')}
              </ManagementTableHeaderCell>
              <ManagementTableHeaderCell>
                {t('trialBooking.colResults')}
              </ManagementTableHeaderCell>
              <ManagementTableHeaderCell>
                {t('trialBooking.colNote')}
              </ManagementTableHeaderCell>
              <ManagementTableHeaderCell sticky>
                {t('trialBooking.colActions')}
              </ManagementTableHeaderCell>
            </TableRow>
          </TableHead>
          {/* ********** */}
          {/* DATA MANIPULATION */}
          {/* ********** */}
          <TableBody>
            {loading ? (
              <TableLoader colSpan={12} />
            ) : (
              <>
                {trialBookingList.map((record) => (
              <TableRow
                key={record.id}
                sx={managementTableRowSx(
                  highlightedRow === `data_${record.id}`,
                )}
                onClick={() => {
                  if (highlightedRow === `data_${record.id}`) {
                    setHighlightedRow(null);
                  } else {
                    setHighlightedRow(`data_${record.id}`);
                  }
                }}
              >
                <TableCell>
                  {new Date(record.createdAt).toLocaleDateString()}
                </TableCell>

                <TableCell
                  sx={{ whiteSpace: 'nowrap' }}
                >{`${record.lastName} ${record.firstName}`}</TableCell>

                <TableCell sx={{ whiteSpace: 'nowrap' }}>
                  {calculateAge(record.dob)}
                </TableCell>

                <TableCell sx={{ whiteSpace: 'nowrap' }}>
                  {new Date(record.dob).toLocaleDateString()}
                </TableCell>

                <TableCell sx={{ whiteSpace: 'nowrap' }}>
                  <ContactValue value={record.email} type="email" />
                </TableCell>

                <TableCell sx={{ whiteSpace: 'nowrap' }}>
                  <ContactValue value={record.phone} type="phone" />
                </TableCell>

                <TableCell sx={{ whiteSpace: 'nowrap' }}>
                  {(() => {
                    const parent = record.parent;
                    // Treat empty AND the literal "None" string as no value.
                    const isEmpty =
                      !parent || parent.trim().toLowerCase() === 'none';
                    return isEmpty ? (
                      <span style={{ opacity: 0.4 }}>
                        {t('trialBooking.none')}
                      </span>
                    ) : (
                      parent
                    );
                  })()}
                </TableCell>

                <TableCell sx={{ whiteSpace: 'nowrap' }}>
                  <ColorChip
                    label={record.subtype.name}
                    palette={serviceTypeColors}
                  />
                </TableCell>

                <TableCell sx={{ whiteSpace: 'nowrap' }}>
                  <div>
                    {dayjs(record.trialDate).format('DD/MM/YYYY - HH:mm')}
                  </div>
                </TableCell>

                <TableCell sx={{ whiteSpace: 'nowrap' }}>
                  <ColorChip
                    label={
                      record.result ? formatResultString(record.result) : null
                    }
                    palette={trialStatusColors}
                    paletteKey={record.result || undefined}
                  />
                </TableCell>

                <TableCell sx={{ whiteSpace: 'nowrap' }}>
                  {(() => {
                    const note = record.note;
                    // Treat empty AND the literal "None" string as no value.
                    const isEmpty =
                      !note || note.trim().toLowerCase() === 'none';
                    return isEmpty ? (
                      <span style={{ opacity: 0.4 }}>
                        {t('trialBooking.none')}
                      </span>
                    ) : (
                      <div>{note}</div>
                    );
                  })()}
                </TableCell>

                <ManagementTableActionCell
                  selected={highlightedRow === `data_${record.id}`}
                >
                  <Tooltip title={t('trialBooking.editTooltip')}>
                    <IconButton
                      color="primary"
                      aria-label="edit registration"
                      onClick={() => {
                        setSelectedTrialBooking(record);
                        setOpenBookingInfoDialog(true);
                      }}
                      disabled={record.result === 'ENROLL'}
                    >
                      <Edit />
                    </IconButton>
                  </Tooltip>
                </ManagementTableActionCell>
              </TableRow>
            ))}
                {trialBookingList.length === 0 ? (
                  <TableNoData />
                ) : (
                  <TableSpacerRow rows={emptyRows} />
                )}
              </>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Other data information */}
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        pl={2}
      >
        <Typography variant="body2" color="textSecondary">
          {t('trialBooking.lastSync')}:{' '}
          {lastSyncTime?.format('DD/MM/YYYY - HH:mm:ss') ||
            t('trialBooking.never')}
        </Typography>
        <TablePagination
          component="div"
          count={totalItems}
          page={page - 1}
          onPageChange={handleChangePage}
          rowsPerPage={limit}
          onRowsPerPageChange={handleChangeRowsPerPage}
          rowsPerPageOptions={[5, 10, 25, 50, 100]}
        />
      </Box>

      {/* Dialog */}
      <TrialBookingInfoDialog
        open={openBookingInfoDialog}
        handleClose={handleCloseTrialBookingDialog}
        trialBooking={selectedTrialBooking}
        fetchTrialBookingList={fetchTrialBookingList}
        subtypeOptions={subtypeOptions}
      />
    </Paper>
  );
};

export default TrialBookingTable;
